import {Injectable} from '@angular/core';
import {Mitarbeiter} from 'shared/dtos/mitarbeiter';
import {Charts} from '../dtos/cockpit/charts/charts';
import {Cockpit} from '../dtos/cockpit/cockpit';
import {DataInput} from '../dtos/data-input';
import {DataResult} from '../dtos/data-result';
import {FileInfo} from '../dtos/fileInfo';
import {FunctionRating} from '../dtos/function-rating';
import {MitarbeiterBase} from '../dtos/mitarbeiterBase';
import {RInputM2} from '../dtos/r-input-m2';
import {ComplianceRiskFunktionsebene} from '../dtos/result/result-m2/compliance-risk-funktionsebene';
import {ComplianceRiskVergleichspaare} from '../dtos/result/result-m2/compliance-risk-vergleichspaare';
import {ExportDetailErgebnis} from '../dtos/result/result-m2/export-detail-ergebnis';
import {Stammdatum} from '../dtos/stammdatum';
import {AdditionalInfoM2} from '../dtos/wizard/additional-info';
import {DataStorageService} from './data-storage.service';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  constructor(private readonly storage: DataStorageService) {}

  public async getInputData() {
    return {
      additionalInfo: await this.storage.getAdditionalInfo(),
      enterpriseInfo: await this.storage.getEnterpriseInfo(),
      fileInfo: await this.storage.getFileInfo(),
      mitarbeiter: await this.storage.getMitarbeiter(),
      funktionen: await this.storage.getFunktionen()
    } as DataInput;
  }

  public async getM2AnalyzerRequestData(): Promise<RInputM2> {
    const ordedObj = await this.getOrderdMitarbeiterByFuncName();
    const dataSanitized = ordedObj.map(e => ({
      ...e,
      laufnummer: ''
    }));
    const funktionen = (await this.storage.getFunktionen()) as Stammdatum[];
    this.normalizeFunktionAndId(dataSanitized, funktionen);

    return {
      mitarbeiter: dataSanitized,
      kontrollmodus: await this.storage.getKontrollmodus()
    } as RInputM2;
  }

  public normalizeFunktionAndId<
    T extends {
      funktion?: string | number | null;
      funktionId?: string | number | null;
    }
  >(list: T[], funktionen: Stammdatum[]): void {
    const valueSet = new Set(
      funktionen.map(f => String(f.value || '').trim()).filter(val => val)
    );
    const captionToValue = new Map(
      funktionen
        .filter(f => f.caption)
        .map(f => [
          f.caption.trim().toLowerCase(),
          String(f.value || '').trim()
        ])
    );

    list.forEach(m => {
      const raw = String(m.funktion || '').trim();
      if (!raw) return;

      const id = valueSet.has(raw)
        ? raw
        : captionToValue.get(raw.toLowerCase());
      if (id) {
        m.funktion = id;
        if ('funktionId' in m) m.funktionId = id;
      } else if ('funktionId' in m && !m.funktionId) {
        m.funktionId = raw;
      }
    });
  }

  public async getIsForModule2() {
    return this.storage.getIsForModule2();
  }

  async getOrderdMitarbeiterByFuncName() {
    const funktionen = (await this.storage.getFunktionen()) as Stammdatum[];
    const mitarbeiter = await this.storage.getMitarbeiter();
    const outputData = this.mergeMitarbeiterFunktionByFncId(
      mitarbeiter,
      funktionen
    );
    outputData.forEach((item, idx, arr) => {
      // SMELL: type should include MitarbeiterForRInputM2
      // eslint-disable-next-line @typescript-eslint/dot-notation
      arr[idx]['sortedIndex'] = idx;

      delete arr[idx].value;
      delete arr[idx].caption;
    });
    return outputData;
  }

  async getFunctionen() {
    return (await this.storage.getFunktionen()) as Stammdatum[];
  }

  async getSortedFunctionen() {
    const funktionen = (await this.storage.getFunktionen()) as Stammdatum[];
    return this.sorted(funktionen, 'caption');
  }

  public async resetData() {
    this.storage.storeFileInfo(new FileInfo());
    this.storage.storeAdditionalInfo(new AdditionalInfoM2());
    this.storage.storeFunktionen(new Array<FunctionRating>());
    this.storage.storeEnterpriseInfo({});
    this.storage.storeDisclaimerRead(false);
    this.storage.storeChartsData(new Charts());
    this.storage.storeCockpitData(new Cockpit());
    this.storage.storeCockpitKontrolle(null);
    this.storage.storeDisableAdditionalInfo(false);
    this.storage.storeUnsavedChanges(false);
    this.storage.storeComplianceRiskVergleichspaare(
      new ComplianceRiskVergleichspaare()
    );
    this.storage.storeComplianceRiskFunktionsebene(
      new ComplianceRiskFunktionsebene()
    );
    try {
      await this.storage.storeMitarbeiter([], false);
      await this.storage.storeKontrollmodusMitarbeiter([]);
    } catch {
      console.warn('IndexedDB not available for reset');
    }
  }

  public async resetResult() {
    this.storage.storeResultData(new DataResult());
    this.storage.storeChartsData(new Charts());
    this.storage.storeCockpitData(new Cockpit());
    this.storage.storeComplianceRiskVergleichspaare(
      new ComplianceRiskVergleichspaare()
    );
    this.storage.storeComplianceRiskFunktionsebene(
      new ComplianceRiskFunktionsebene()
    );
    this.storage.storeExportDetailergebnis(new ExportDetailErgebnis());
    try {
      await this.storage.storeKontrollmodusMitarbeiter([]);
    } catch {
      console.warn(
        'IndexedDB not available for reset kontrollmodusMitarbeiter items'
      );
    }
  }

  mergeMitarbeiterFunktionByFncId(
    mitarbeiters: MitarbeiterBase[],
    funktionen: Stammdatum[]
  ): (MitarbeiterBase & Partial<Stammdatum>)[] {
    const mergedMitarbeiter = mitarbeiters.map(m => {
      const fnc = funktionen.find(f => f.value === m.funktion);
      const ms: MitarbeiterBase & Partial<Stammdatum> = m;
      if (fnc) {
        ms.caption = fnc.caption;
      }
      return ms;
    });

    return this.SortedMitartbeiterByFuncName(mergedMitarbeiter);
  }

  public sorted<T>(source: T[], propName: string) {
    return source.sort((a, b) => {
      if (a[propName]) {
        return b[propName] ? a[propName].localeCompare(b[propName]) : -1;
      } else if (b[propName]) {
        return a[propName] ? b[propName].localeCompare(a[propName]) : 1;
      }
      return 0;
    });
  }

  public setMaxAnforderungsNiveau(mitarbeiters: Mitarbeiter[]) {
    const maxAnfNiveau = mitarbeiters
      .map(person => person.betrieblichesKompetenzniveau)
      .reduce((prev, current) => (prev && prev > current ? prev : current));

    this.storage.storeMaxAnforderungsNiveau(Number(maxAnfNiveau));
  }

  async hasData(): Promise<boolean> {
    const mitarbeiter = await this.storage.getMitarbeiter();
    return mitarbeiter.length > 0;
  }

  private SortedMitartbeiterByFuncName(
    mergedMitarbeiter: (MitarbeiterBase & Partial<Stammdatum>)[]
  ) {
    mergedMitarbeiter = mergedMitarbeiter.sort((a, b) =>
      Intl.Collator([], {
        numeric: true,
        sensitivity: 'base'
      }).compare(a.caption, b.caption)
    );
    return mergedMitarbeiter;
  }

  // TODO transforme function id to caption
  private setTransformedFncIdToLabel<TData>(
    source: TData[],
    funktion: FunctionRating,
    propName: string
  ) {
    const arr = source.filter(fnc => fnc[propName] === funktion.value);
    if (arr.length > 0) {
      arr.forEach(c => {
        c[propName] = funktion.caption;
      });
    }
    return arr;
  }
}
